import ResponsiveImage from "../responsive-image/ResponsiveImage";
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./ProductDetail.css";

import instrumentsData from "../../instrumente/instruments.js";
import enTranslations from "./en.json";
import deTranslations from "./de.json";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  // Add translations for this component
  i18n.addResourceBundle("en", "shop", enTranslations, true, true);
  i18n.addResourceBundle("de", "shop", deTranslations, true, true);

  // Helper function to generate placeholder images
  const getPlaceholderImage = (item, imageIndex) => {
    const title = i18n.language === "en" ? item.title_en : item.title_de;
    const views = ["Vorderseite", "Rückseite", "Seitlich", "Detail"];
    const viewName = views[imageIndex] || `View ${imageIndex + 1}`;
    return `https://via.placeholder.com/800x1200/8B4513/FFFFFF?text=${encodeURIComponent(
      title + " " + viewName
    )}`;
  };

  // Find product by ID from instruments data
  const instrumentData = instrumentsData.find(
    (item) => item.id === parseInt(id)
  );

  if (!instrumentData) {
    return (
      <main className="product-detail-error" role="main">
        <section role="alert" aria-live="assertive">
          <h1>{t("shop:not_found", "Produkt nicht gefunden")}</h1>
          <button
            onClick={() => navigate(-1)}
            className="back-button focus-highlight"
            tabIndex={0}
            aria-label={t("shop:back_to_shop", "Zurück zum Shop")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(-1);
              }
            }}
          >
            {t("shop:back_home", "Zurück")}
          </button>
        </section>
      </main>
    );
  }

  // Create product object from instrument data
  const product = {
    id: instrumentData.id,
    title:
      i18n.language === "en"
        ? instrumentData.title_en
        : instrumentData.title_de,
    description:
      i18n.language === "en"
        ? instrumentData.detail_text_en
        : instrumentData.detail_text_de,
    images: instrumentData.bilder.map((_, index) =>
      getPlaceholderImage(instrumentData, index)
    ),
  };

  const handleBackClick = () => {
    // Use browser's native back navigation to restore scroll position
    navigate(-1);
  };

  if (!product) {
    return (
      <main className="product-detail-error" role="main">
        <section role="alert" aria-live="assertive">
          <h1>{t("productDetail:not_found", "Produkt nicht gefunden")}</h1>
          <button
            onClick={handleBackClick}
            className="back-button focus-highlight"
            tabIndex={0}
            aria-label={t("shop:back_to_homepage", "Zurück zur Startseite")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleBackClick();
              }
            }}
          >
            {t("productDetail:back_home", "Zurück zur Startseite")}
          </button>
        </section>
      </main>
    );
  }

  const handleContactClick = () => {
    const subject = encodeURIComponent(
      i18n.language === "en"
        ? `Interest in ${product.title}`
        : `Interesse an ${product.title}`
    );
    const body = encodeURIComponent(
      i18n.language === "en"
        ? `Dear Sir or Madam,\n\nI am interested in the instrument "${product.title}".\n\nPlease contact me for further information.\n\nBest regards`
        : `Sehr geehrte Damen und Herren,\n\nich interessiere mich für das Instrument "${product.title}".\n\nBitte kontaktieren Sie mich für weitere Informationen.\n\nMit freundlichen Grüßen`
    );
    window.location.href =
      i18n.language === "en"
        ? `/en/contact?subject=${subject}&body=${body}`
        : `/contact?subject=${subject}&body=${body}`;
  };

  return (
    <main className="product-detail-page" role="main">
      <div className="product-detail-container">
        <div className="product-detail-content">
          <section
            className="product-images-section"
            role="region"
            aria-labelledby="product-images-heading"
          >
            <h2 id="product-images-heading" className="sr-only">
              {t("shop:product_images", "Produktbilder")}
            </h2>
            <div
              className="main-image"
              role="img"
              aria-labelledby="main-image-description"
            >
              <ResponsiveImage sizes="(max-width: 768px) 100vw, 50vw"
                src={product.images[currentImageIndex]}
                loading="eager"
                alt={t(
                  "shop:main_image_alt",
                  "{{title}} - Detailansicht {{viewNumber}} von {{totalViews}}",
                  {
                    title: product.title,
                    viewNumber: currentImageIndex + 1,
                    totalViews: product.images.length,
                  }
                )}
                className="main-product-image"
                id="main-image-description"
              />
            </div>
            <div
              className="image-thumbnails"
              role="group"
              aria-label={t("shop:image_thumbnails", "Produktbild-Navigation")}
            >
              {product.images.map((image, index) => (
                <button
                  key={index}
                  type="button"
                  tabIndex={0}
                  aria-pressed={index === currentImageIndex}
                  className={`thumbnail focus-highlight ${
                    index === currentImageIndex ? "active" : ""
                  }`}
                  onClick={() => setCurrentImageIndex(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setCurrentImageIndex(index);
                    }
                  }}
                  aria-label={t(
                    "shop:thumbnail_aria",
                    "Bild {{number}} von {{total}} anzeigen: {{title}}",
                    {
                      number: index + 1,
                      total: product.images.length,
                      title: product.title,
                    }
                  )}
                >
                  <ResponsiveImage sizes="100px" src={image} alt="" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>

          <section
            className="product-info-section"
            role="region"
            aria-labelledby="product-info-heading"
          >
            <div className="product-content">
              <h1
                id="product-info-heading"
                className="product-title keyboard-focusable focus-highlight"
                tabIndex={0}
                role="text"
                aria-label={t(
                  "shop:product_title_aria",
                  "Produkttitel: {{title}}",
                  { title: product.title }
                )}
                style={{ pointerEvents: "none" }}
                onFocus={(e) => {
                  e.target.style.pointerEvents = "auto";
                }}
                onBlur={(e) => {
                  e.target.style.pointerEvents = "none";
                }}
              >
                {product.title}
              </h1>

              <div
                className="product-description keyboard-focusable focus-highlight"
                tabIndex={0}
                aria-label={`${t(
                  "shop:product_description_aria",
                  "Produktbeschreibung"
                )}: ${product.description}`}
                aria-live="polite"
                style={{ pointerEvents: "none" }}
                onFocus={(e) => {
                  e.target.style.pointerEvents = "auto";
                  // Force screen reader to read content
                  e.target.setAttribute("aria-live", "assertive");
                  setTimeout(() => {
                    e.target.setAttribute("aria-live", "polite");
                  }, 100);
                }}
                onBlur={(e) => {
                  e.target.style.pointerEvents = "none";
                }}
              >
                <p>{product.description}</p>
              </div>
            </div>

            <div
              className="product-actions"
              role="group"
              aria-label={t("shop:product_actions", "Produktaktionen")}
            >
              <div className="button-group">
                <button
                  onClick={handleBackClick}
                  className="back-button focus-highlight"
                  tabIndex={0}
                  aria-label={t("shop:back_aria", "Zurück zur Produktliste")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleBackClick();
                    }
                  }}
                >
                  <span>← {t("shop:back", "Zurück")}</span>
                </button>
                <button
                  className="contact-button focus-highlight"
                  onClick={handleContactClick}
                  tabIndex={0}
                  aria-label={t(
                    "shop:contact_aria",
                    "Anfrage für {{title}} stellen",
                    { title: product.title }
                  )}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleContactClick();
                    }
                  }}
                >
                  <span>{t("shop:contact_button", "Anfrage stellen")}</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
